import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { PayrollReturnRouteService } from '@app/+authenticated/+payroll/payroll-navigation';
import { SidePanelService } from '@app/+authenticated/shared/app-side-panel/side-panel.service';
import { OnboardingDraftScheduleService } from '@app/+onboarding/draft-schedule/onboarding-draft-schedule.service';
import { AppState } from '@app/reducers/interfaces';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { isUserGuideLoaded } from '@app/reducers/orm/user-guide/user-guide.selector';
import { FeatureFlagQueryParamService } from '@app/services/feature-flag-query-param.service';
import { InAppMessageActionService } from '@app/services/in-app-message-action.service';
import { RefinerService } from '@app/services/refiner.services';
import { TrackingService } from '@app/services/tracking.service';
import { Store } from '@ngrx/store';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { UserConfigService } from '@sb/shared/user-config';
import { BreakpointObserver, Breakpoints } from '@sb/ui/breakpoints';
import { SbDialogService } from '@sb/ui/dialog';
import { clsx } from 'clsx';
import { filter, take } from 'rxjs';

import { FirstTouchComponent } from './shared/first-touch/first-touch.component';
import { FirstTouchService } from './shared/first-touch/first-touch.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-authenticated',
  templateUrl: './authenticated.component.html',
  host: { '[class]': 'hostClasses' },
})
export class AuthenticatedComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private feature = inject(FeatureService);
  private refinerService = inject(RefinerService);
  private destroyRef = inject(DestroyRef);
  private firstTouchService = inject(FirstTouchService);
  private trackingService = inject(TrackingService);
  private sidePanelService = inject(SidePanelService);

  #userConfig = inject(UserConfigService);
  #breakpointObserver = inject(BreakpointObserver);
  #dialog = inject(SbDialogService);
  #payrollReturnRoute = inject(PayrollReturnRouteService);
  hostClasses = clsx('relative flex h-full flex-1 flex-col');

  authUser$ = this.store.select(getAuthenticatedUser);

  cmdKEnabled = toSignal(this.feature.isEnabled$(featureFlag.LABS_COMMAND_PALETTE));

  #userConfigSideNavMinimized = signal(!this.#userConfig.get('sideNavOpen', true)());
  #viewportForcesSideNavMinimized = toSignal(this.#breakpointObserver.observeMatched(Breakpoints['max-xl']), {
    initialValue: false,
  });

  /**
   * Effective state bound to the sidebar UI.
   *
   * `linkedSignal` resets to `source || userPref` whenever the viewport crosses the breakpoint, but
   * remains writable so a user toggle can override the derived value. The manual value survives
   * until the next viewport crossing — after which the derived computation takes over again.
   */
  sideNavMinimized = linkedSignal({
    source: () => this.#viewportForcesSideNavMinimized(),
    computation: (forced) => forced || untracked(() => this.#userConfigSideNavMinimized()),
  });

  sidePanelContent = this.sidePanelService.activeComponent;
  sidePanelContentInputs = this.sidePanelService.activeComponentInputs;
  sidePanelSize = this.sidePanelService.activeSize;
  isUserGuideLoaded = this.store.selectSignal(isUserGuideLoaded);
  #draftSchedule = inject(OnboardingDraftScheduleService);
  draftScheduleActive = this.#draftSchedule.isActive;
  // The first-touch card *replaces* the routed page, so suppress it while an
  // AI-inferred draft is pending or active — otherwise on a fresh account the card
  // can win the arrival race, the schedule page never mounts, and the draft panel
  // never gets to show. Gate on the pending recommendation (true before the page
  // mounts, when isActive() is still false) as well as the active flag, so this
  // doesn't depend on the first-touch track round-trip landing first.
  shouldShowFirstTouch = computed(
    () =>
      this.firstTouchService.shouldShowFirstTouch() &&
      !this.#draftSchedule.hasPendingRecommendation() &&
      !this.draftScheduleActive(),
  );
  firstTouchConfig = this.firstTouchService.firstTouchConfig;
  firstTouchView = viewChild(FirstTouchComponent);

  constructor() {
    // Eager-instantiate so enabled feature flags are reflected as URL query
    // params for authenticated users.
    inject(FeatureFlagQueryParamService);

    // Eager-instantiate so Customer.io in-app message actions (e.g. enabling
    // Labs) run their side effects for authenticated users.
    inject(InAppMessageActionService);

    // Persist only the user explicit toggles, not the responsive override.
    effect(() => {
      this.#userConfig.set('sideNavOpen', !this.#userConfigSideNavMinimized());
    });

    void this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ urlAfterRedirects }) => {
        // `urlAfterRedirects`, not `url`: the manager has to be sent back somewhere they actually
        // landed, and `url` is what was requested before any guard redirect.
        this.#payrollReturnRoute.track(urlAfterRedirects);
      });

    effect(() => {
      if (this.firstTouchConfig() && this.firstTouchView()) {
        untracked(() => {
          void this.trackingService.event('First Touch Viewed', {
            first_touch_type: this.firstTouchService.activeFirstTouchModule(),
          });
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.feature.isEnabled(featureFlag.REFINER)) {
      this.refinerService.init();
    }
  }

  ngOnDestroy(): void {
    // Close any lingering dialogs when the authenticated area is torn down
    // (logout, session expiry, redirects to `/login`, `/trial-expired`, etc.).
    this.#dialog.closeAll();
  }

  onFirstTouchButtonClick() {
    this.trackingService
      .event('First Touch CTA Clicked', {
        first_touch_type: this.firstTouchService.activeFirstTouchModule(),
      })
      .finally(() => {
        void this.firstTouchService.onFirstTouchButtonClick().pipe(take(1)).subscribe();
      });
  }

  dismissSidePanel() {
    this.sidePanelService.close();
  }

  onSideNavMinimizedChange(minimized: boolean) {
    this.sideNavMinimized.set(minimized);
    this.#userConfigSideNavMinimized.set(minimized);
  }
}
